import { Field, Float, ID, InputType, ObjectType } from 'type-graphql'
import { Prop } from '@typegoose/typegoose'
import { Product } from '../product/Product'
import { RelatedEntity } from '../base/RelatedEntity'
import { generateEntityId } from '../generateEntityId'
import { ReturnItemResolutionEnum } from './ReturnItemResolutionEnum'

/**
 * Dual-decorated as both the persisted/returned entity and the `ReturnOrderItemInput` GraphQL input
 * (same pattern as `OrderItem`/`OrderItemInput`), so create/update mutations reuse this single model.
 * The client supplies only the intent fields (`orderItemId`, `quantity`, `reason`, `resolution`,
 * `restock`, optional `serialNumbers`); the server-derived fields (`_id`, `product`, `price`, `net`,
 * `gross`) are optional inputs that `ReturnOrderLineBuilderService` recomputes from the source order
 * and overwrites — any client-supplied values for them are ignored, so they can never tamper with money.
 */
@ObjectType()
@InputType('ReturnOrderItemInput')
export class ReturnOrderItem {
  @Field(() => ID, { nullable: true })
  @Prop({ type: String, required: true, default: () => generateEntityId('roit') })
  _id?: string

  @Field(() => Product, { nullable: true })
  @Prop({ type: () => RelatedEntity })
  product?: ReturnType<() => Product>

  /**
   * The source OrderItem._id this returned line came from.
   */
  @Field(() => String)
  @Prop({ type: String })
  orderItemId: string

  @Field(() => Float)
  @Prop({ type: Number })
  quantity: number

  @Field(() => Float, { nullable: true })
  @Prop({ type: Number })
  price?: number

  @Field(() => Float, { nullable: true })
  @Prop({ type: Number })
  net?: number

  @Field(() => Float, { nullable: true })
  @Prop({ type: Number })
  gross?: number

  @Field(() => String)
  @Prop({ type: String })
  reason: string

  @Field(() => ReturnItemResolutionEnum)
  @Prop({ type: String, enum: ReturnItemResolutionEnum })
  resolution: ReturnItemResolutionEnum

  /**
   * Honored only when resolution = Refund (goods physically returned).
   */
  @Field(() => Boolean)
  @Prop({ type: Boolean })
  restock: boolean

  /**
   * Returned serial numbers for serial-tracked products; count equals quantity.
   */
  @Field(() => [String], { nullable: true })
  @Prop({ type: [String] })
  serialNumbers?: string[]
}
